import React, { useState, useEffect, useCallback } from 'react';
import { elegantMenuProps } from '../utils/menuStyles';
import {
  Box, Typography, Button, Paper, Grid, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, IconButton, Menu, MenuItem, ListItemIcon,
  ListItemText, TextField, useTheme,
  Skeleton, InputAdornment, Tooltip, LinearProgress, Stack, Switch, FormControlLabel,
} from '@mui/material';
import {
  Add, MoreVert, Visibility, Block, Search, TrendingUp, Undo,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { useDebounce } from '../hooks/useDebounce';
import { useModulePermissions } from '../hooks/useModulePermissions';
import { useAuth } from '../context/AuthContext';
import RevenueReportDocument from '../components/finance/pdf/RevenueReportDocument';
import { usePdfExport, useSchoolBranding } from '../utils/pdf';
import revenueService from '../services/revenueService';
import revenueCategoryService from '../services/revenueCategoryService';
import { Revenue, RevenueCategory } from '../types/revenues';
import CreateRevenueDialog from '../components/revenues/CreateRevenueDialog';
import ReasonDialog from '../components/common/ReasonDialog';
import { useCurrencyFormat } from '../hooks/useCurrencyFormat';
import { useTimezone } from '../hooks/useTimezone';
import ViewRevenueDialog from '../components/revenues/ViewRevenueDialog';
import SearchableSelect, { SelectOption } from '../components/common/SearchableSelect';
import TagChip from '../components/common/TagChip';
import { ToneChip } from '../components/common/StatusChip';
import { tagColor } from '../utils/tagColor';
import { PAYMENT_METHOD_LABEL } from '../utils/paymentMethods';
import DownloadMenu from '../components/common/DownloadMenu';

/**
 * RevenuesPage — the money-in view.
 *
 * No approval workflow. Recording a revenue posts it straight to the ledger.
 * If it's wrong, click Reverse (row menu) and give a reason — that soft-
 * deletes the row and posts a compensating OUT ledger entry. The original
 * row is preserved as an audit trail.
 */
const RevenuesPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { canRead, canWrite } = useModulePermissions();
  const { currency } = useAuth();
  const money = useCurrencyFormat();
  const { formatDate } = useTimezone();

  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [revenues, setRevenues] = useState<Revenue[]>([]);
  const [categories, setCategories] = useState<RevenueCategory[]>([]);
  const [total, setTotal] = useState(0);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchFilter, setSearchFilter] = useState('');
  const debouncedSearch = useDebounce(searchFilter, 300);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [includeReversed, setIncludeReversed] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [selectedRevenue, setSelectedRevenue] = useState<Revenue | null>(null);

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRevenue, setMenuRevenue] = useState<Revenue | null>(null);

  const loadRevenues = useCallback(async () => {
    setLoading(true);
    try {
      const res = await revenueService.getRevenues({
        categoryId: categoryFilter || undefined,
        method: methodFilter || undefined,
        search: debouncedSearch.trim() || undefined,
        includeReversed,
        page: page + 1,
        limit: rowsPerPage,
      });
      setRevenues(res.revenues);
      setTotal(res.pagination.totalItems);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load revenues', { variant: 'error' });
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  }, [categoryFilter, methodFilter, debouncedSearch, includeReversed, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => { loadRevenues(); }, [loadRevenues]);
  useEffect(() => {
    revenueCategoryService.getCategories().then(setCategories).catch(() => setCategories([]));
  }, []);
  useEffect(() => { setPage(0); }, [debouncedSearch, categoryFilter, methodFilter, includeReversed]);

  // ─── Actions ──────────────────────────────────────────────────────────────

  const handleCreate = async (formData: FormData) => {
    try {
      await revenueService.createRevenue(formData);
      enqueueSnackbar('Revenue recorded and posted to the ledger', { variant: 'success' });
      loadRevenues();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to record revenue', { variant: 'error' });
      throw err;
    }
  };

  const handleReverse = async (reason: string) => {
    if (!selectedRevenue) return;
    await revenueService.reverseRevenue(selectedRevenue._id, reason);
    enqueueSnackbar('Income reversed', { variant: 'success' });
    loadRevenues();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, revenue: Revenue) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuRevenue(revenue);
  };
  const handleMenuClose = () => { setMenuAnchor(null); setMenuRevenue(null); };

  // Filter dropdown options for the SearchableSelects below.
  const revenueCategoryOptions: SelectOption[] = [
    { value: '', label: 'All' },
    ...categories.map(c => ({ value: c._id, label: c.name })),
  ];
  const revenueMethodOptions: SelectOption[] = [
    { value: '', label: 'All' },
    ...(Object.keys(PAYMENT_METHOD_LABEL) as (keyof typeof PAYMENT_METHOD_LABEL)[])
      .map(m => ({ value: m, label: PAYMENT_METHOD_LABEL[m] })),
  ];

  // ─── Guards ───────────────────────────────────────────────────────────────

  // The PDF is drawn in the browser from the filtered report; the CSV comes from the server.
  const { generate: generatePdf } = usePdfExport();
  const { branding } = useSchoolBranding();
  const exportAs = async (kind: 'pdf' | 'csv') => {
    const filters = { categoryId: categoryFilter || undefined, method: methodFilter || undefined, search: debouncedSearch.trim() || undefined, includeReversed };
    if (kind === 'csv') return revenueService.exportRevenuesCsv(filters);
    await generatePdf(async () => (
      <RevenueReportDocument
        report={await revenueService.getRevenueReport(filters)}
        currency={currency || 'PKR'}
        branding={branding}
        generatedAt={new Date().toISOString()}
      />
    ), `revenue-report-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
  };

  if (!canRead('revenues')) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <Paper elevation={0} sx={{ p: 5, textAlign: 'center', maxWidth: 420, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
          <Block sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight={500}>Access Denied</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            You don&apos;t have permission to view Revenues.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (initialLoad && loading) {
    return (
      <Box>
        <Skeleton variant="text" width={250} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  const hasFilters = !!(searchFilter || categoryFilter || methodFilter);


  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>Revenue</Typography>
          <Typography variant="body2" color="text.secondary">
            Money received outside fees — donations, grants, rent, sales. Fees and fines are recorded in their own screens. Posts to the ledger instantly.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <DownloadMenu onExport={exportAs} />
          {canWrite('revenues') && (
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => setCreateOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Record Revenue
            </Button>
          )}
        </Stack>
      </Box>

      {/* Filter bar */}
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 3, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
      >
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by title…"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <SearchableSelect
              label="Category"
              value={categoryFilter}
              onChange={(v) => setCategoryFilter(v)}
              options={revenueCategoryOptions}
              placeholder="All categories"
              fullWidth
              minWidth={0}
              renderOptionAdornment={(opt) => opt.value ? (
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tagColor(opt.label) }} />
              ) : null}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <SearchableSelect
              label="Method"
              value={methodFilter}
              onChange={(v) => setMethodFilter(v)}
              options={revenueMethodOptions}
              placeholder="All methods"
              fullWidth
              minWidth={0}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }} sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControlLabel
              control={<Switch size="small" checked={includeReversed} onChange={(_, v) => setIncludeReversed(v)} />}
              label={<Typography variant="body2">Include reversed</Typography>}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden', position: 'relative' }}
      >
        {loading && revenues.length > 0 && (
          <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 1 }} />
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sub-Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Received from</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {revenues.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 8 }}>
                    <TrendingUp sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      {hasFilters ? 'No revenues match your filters' : 'No revenues yet'}
                    </Typography>
                    {!hasFilters && canWrite('revenues') && (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                        Click <b>Record Revenue</b> to add your first entry.
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : revenues.map(rev => (
                <TableRow
                  key={rev._id}
                  hover
                  onClick={() => { setSelectedRevenue(rev); setViewOpen(true); }}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={500} color={rev.isActive ? 'text.primary' : 'text.secondary'}>{rev.title}</Typography>
                      {!rev.isActive && <ToneChip tone="error" size="small" label="Reversed" />}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">{currency || 'PKR'}</Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ color: rev.isActive ? theme.palette.success.main : theme.palette.text.disabled, textDecoration: rev.isActive ? 'none' : 'line-through', fontFeatureSettings: '"tnum" 1' }}
                      >
                        {rev.amount.toLocaleString()}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell><Typography variant="body2">{format(new Date(rev.date), 'MMM dd, yyyy')}</Typography></TableCell>
                  <TableCell>
                    <TagChip value={rev.categoryId?.name} />
                  </TableCell>
                  <TableCell>
                    <TagChip value={rev.subCategoryId?.name} />
                  </TableCell>
                  <TableCell>
                    {rev.counterparty?.name ? (
                      <Stack spacing={0}>
                        <Typography variant="body2">{rev.counterparty.name}</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ textTransform: 'capitalize' }}>
                          {rev.counterparty.kind}
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <TagChip value={PAYMENT_METHOD_LABEL[rev.method]} />
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Actions">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, rev)}>
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Row menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose} {...elegantMenuProps} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => { setSelectedRevenue(menuRevenue); setViewOpen(true); handleMenuClose(); }}>
          <ListItemIcon><Visibility fontSize="small" /></ListItemIcon>
          <ListItemText>View</ListItemText>
        </MenuItem>
        {canWrite('revenues') && menuRevenue?.isActive && (
          <MenuItem
            onClick={() => { setSelectedRevenue(menuRevenue); setReverseOpen(true); handleMenuClose(); }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon><Undo fontSize="small" color="error" /></ListItemIcon>
            <ListItemText>Reverse</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Dialogs */}
      <CreateRevenueDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />
      <ViewRevenueDialog
        open={viewOpen}
        revenue={selectedRevenue}
        onClose={() => { setViewOpen(false); setSelectedRevenue(null); }}
      />

      <ReasonDialog
        open={reverseOpen}
        onClose={() => { setReverseOpen(false); setSelectedRevenue(null); }}
        title="Reverse this income?"
        facts={selectedRevenue ? [
          { label: 'Title', value: selectedRevenue.title },
          { label: 'Amount', value: money(selectedRevenue.amount) },
          { label: 'Date', value: formatDate(selectedRevenue.date) },
        ] : []}
        consequence="This income no longer counts and the reversal is recorded in the books today. The entry stays on record, marked reversed."
        confirmText="Reverse income"
        onConfirm={handleReverse}
      />
    </Box>
  );
};

export default RevenuesPage;
